import {
  GridToolbarContainer,
} from "@mui/x-data-grid";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CustomGridToolbarQuickFilter from "../CustomGridToolbarQuickFilter";
import { StyledDataGrid } from "../../../components/StyledComponents";
import CustomPagination from "../CustomPagination";
import NoRowOverlay from "../../../assets/images/icons/NoRowOverlay";
import EditNew20pxSvg from "../../../assets/images/icons/EditNew20pxSvg";
import DeleteIcon16pxSvg from "../../../assets/images/icons/DeleteIcon16pxSvg";
import { format } from "date-fns";
import LeaveApi from "./../../../axios/LeaveAndHoliday/LeaveApi";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import Cookies from "js-cookie";
import { useAuth } from "../../../context/AuthContext";
import { generateTranslateMuiTable } from "../../../utils/Function/commonFunction";
import { useLoading } from "../../../context/LoadingContext";
import { getUserList } from "../../../axios/User/UserApi";
import CustomColumnsPanel from "../../../components/CustomColumnsPanel";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import DetailRequestDialogAR from "./DetailRequestDialogAR";
import DetailRequestDialog from "./DetailRequestDialog";
import EditLeaveDialog from "./EditLeaveDialog";
import DeleteLeaveDialog from "./DeleteLeaveDialog";
import { getShiftListAll } from "../../../axios/Shift/ShiftApi";
import { useRefresh } from "../../../context/RefreshContext";

dayjs.extend(utc);
dayjs.extend(timezone);

const DEFAULT_FULL_DAY_MINUTES = 8 * 60;

const parseTimeStringToMinutes = (timeStr) => {
  if (!timeStr) return 0;
  const parts = String(timeStr).split(":").map(Number);
  const [h = 0, m = 0, s = 0] = parts;
  return h * 60 + m + Math.floor(s / 60);
};

/** Ưu tiên numOfTime; fallback numOfDay (dữ liệu cũ) → số ngày hiển thị */
const resolveDisplayDays = (data) => {
  if (!data) return null;
  if (data.numOfTime) {
    const timeMins = parseTimeStringToMinutes(data.numOfTime);
    const dayMins =
      parseTimeStringToMinutes(data.hoursPerLeaveDaySnapshot) ||
      DEFAULT_FULL_DAY_MINUTES;
    if (dayMins <= 0) return 0;
    const days = timeMins / dayMins;
    return Math.round(days * 100) / 100;
  }
  if (data.numOfDay != null && data.numOfDay !== "") {
    return Number(data.numOfDay);
  }
  return null;
};

const LeavesGridInfoTable = (props) => {
  const {
    refreshData,
    onRowSelection,
    formatLeaveFilter,
    selectedDate,
    setTotalUserLeave,
    setPendingRequestCount,
    handleGetCountData,
    statusShowReject,
    leaveTypesData,
    gridMode = 'requests',
  } = props;
  const { getLeaveList, getLeaveRequestList } = LeaveApi();
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const { profile, isAdmin, dateTimeFormat, genaralSettingTimeZone } = useAuth();
  const { handleRefreshData } = useRefresh();
  const tableTranslated = useMemo(() => generateTranslateMuiTable(t), [t]);
  const [show, hide] = useLoading();
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [allUser, setAllUser] = useState([]);
  const [shiftsData, setShiftsData] = useState([]);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [openDetailAR, setOpenDetailAR] = useState(false);
  const [openDetail, setOpenDetail] = useState(false);
  const [openEditLeave, setOpenEditLeave] = useState(false);
  const [openDeleteLeave, setOpenDeleteLeave] = useState(false);

  const isLeavesMode = gridMode === 'leaves';
  const isRequestsMode = gridMode === 'requests';
  const fmt = dateTimeFormat || "dd/MM/yyyy HH:mm:ss";

  const renderHeaderWithTooltip = (params) => (
    <Tooltip title={params.colDef.headerName} arrow>
      <span>{params.colDef.headerName}</span>
    </Tooltip>
  );

  const renderCellWithTooltip = (params) => {
    const value =
      params.formattedValue !== undefined && params.formattedValue !== null
        ? params.formattedValue
        : params.value ?? "-";

    return (
      <Tooltip title={String(value)} arrow>
        <span className="truncate">{String(value)}</span>
      </Tooltip>
    );
  };

  const formatWithCompanyTZ = (value, formatStr) => {
    if (!value) return "-";

    return format(new Date(value), formatStr);
  };

  /** Parse newData (object hoặc JSON string) từ leave-request */
  const parseNewData = (newData) => {
    if (!newData) return {};
    if (typeof newData === "string") {
      try {
        return JSON.parse(newData) || {};
      } catch {
        return {};
      }
    }
    return newData;
  };

  /** Map item leave-request → row grid (fullName, start/end/leaveTypeId từ newData) */
  const mapLeaveRequestToRow = useCallback((item, index) => {
    const newData = parseNewData(item?.newData);
    const oldData = parseNewData(item?.oldData);
    const requestType = String(item?.requestType || "").toUpperCase();
    const displayData = requestType === "DELETE_LEAVE" ? oldData : newData;
    const requestStatusId = item?.requestStatusId ?? item?.requestStatus ?? null;

    return {
      ...item,
      id: item?.id ?? item?.leaveRequestId ?? index + 1,
      leaveId: item?.leaveId ?? item?.leaveRequestId ?? item?.id ?? index + 1,
      leaveRequestId: item?.leaveRequestId ?? item?.id ?? item?.leaveId ?? index + 1,
      fullName: item?.fullName || item?.name || "-",
      requestStartDate: displayData.start || item?.start || null,
      requestEndDate: displayData.end || item?.end || null,
      leaveTypeId: displayData.leaveTypeId ?? item?.leaveTypeId ?? null,
      requestStatusId,
      // Đồng bộ với cột approvalStatus hiện có
      approvalStatusId: requestStatusId,
      userId: item?.userId ?? displayData.userId ?? null,
      requestReason: item?.requestReason ?? displayData.requestReason ?? "",
      responseReason: item?.responseReason ?? displayData.responseReason ?? "",
      numOfTime: displayData.numOfTime ?? item?.numOfTime ?? null,
      hoursPerLeaveDaySnapshot:
        displayData.hoursPerLeaveDaySnapshot ??
        item?.hoursPerLeaveDaySnapshot ??
        null,
      // Số ngày quy đổi từ numOfTime (UI vẫn dùng nhãn "days")
      numOfDay: resolveDisplayDays({
        numOfTime: displayData.numOfTime ?? item?.numOfTime,
        hoursPerLeaveDaySnapshot:
          displayData.hoursPerLeaveDaySnapshot ??
          item?.hoursPerLeaveDaySnapshot,
        numOfDay: displayData.numOfDay ?? item?.numOfDay,
      }),
      leavePolicyId: displayData.leavePolicyId ?? item?.leavePolicyId ?? null,
      shiftId: displayData.shiftId ?? item?.shiftId ?? null,
      oldData,
      newData,
    };
  }, []);

  const mapRowToLeaveInfo = useCallback((row) => ({
    leaveId: row.leaveId ?? row.leaveRequestId,
    leaveRequestId: row.leaveRequestId ?? row.leaveId,
    employeeCode: row.employeeCode,
    name: row.fullName || row.name,
    requestStartDate: formatWithCompanyTZ(row.requestStartDate, fmt),
    requestEndDate: formatWithCompanyTZ(row.requestEndDate, fmt),
    numOfTime: row.numOfTime,
    hoursPerLeaveDaySnapshot: row.hoursPerLeaveDaySnapshot,
    numOfDay: row.numOfDay,
    leaveType: row.leaveType,
    leavePolicy: row.leavePolicy,
    approvalStatus: row.approvalStatus,
    approveStartDate: row.approveStartDate,
    approveEndDate: row.approveEndDate,
    requestReason: row.requestReason,
    responseReason: row.responseReason,
    leavePolicyId: row.leavePolicyId,
    leaveTypeId: row.leaveTypeId,
    requestType: row.requestType,
    approvalStatusId: row.approvalStatusId ?? row.requestStatusId,
    requestStatusId: row.requestStatusId ?? row.approvalStatusId,
    userId: row.userId,
    modifiedBy: row.modifiedBy,
  }), [fmt]);

  const getLeaveTypeLabel = useCallback(
    (leaveTypeId) => {
      if (!leaveTypeId || !leaveTypesData?.length) return "-";

      const leaveType = leaveTypesData.find(
        (lt) => String(lt.leaveTypeId) === String(leaveTypeId)
      );

      if (!leaveType) return "-";

      const langId = Number(localStorage.getItem("i18nextLngId"));

      if (langId === 2) {
        return leaveType.remarkEN || leaveType.remark || "-";
      }

      // default langId === 1
      return leaveType.remark || leaveType.remarkEN || "-";
    },
    [leaveTypesData]
  );

  const getRequestTypeLabel = useCallback((requestType) => {
    const normalizedType = String(requestType || "").toUpperCase();

    if (normalizedType === "ADD_LEAVE") return t("common.request-type-add-leave");
    if (normalizedType === "EDIT_LEAVE") return t("common.request-type-edit-leave");
    if (normalizedType === "DELETE_LEAVE") return t("common.request-type-delete-leave");

    return "-";
  }, [t]);

  const getFullNameByEmail = useCallback((email) => {
    if (!email) return "-";

    const normalizedEmail = String(email).trim().toLowerCase();

    const user = allUser.find(
      (u) =>
        u.email &&
        u.email.trim().toLowerCase() === normalizedEmail
    );

    return user?.fullName || email;
  }, [allUser]);

  const baseColumns = useMemo(
    () => [
      {
        field: "fullName",
        headerName: t("common.fullname"),
        minWidth: 220,
      },
      {
        field: "requestStartDate",
        headerName: t("common.start-date"),
        flex: 1,
        rowSpanValueGetter: (value, row) =>
          row ? `${row.id}-${row.requestStartDate}` : value,
        valueFormatter: (value) => {
          if (value === null || value === undefined) return "-";

          return value
            ? formatWithCompanyTZ(
              value,
              dateTimeFormat || "dd/MM/yyyy HH:mm:ss"
            )
            : "-";
        },
      },
      {
        field: "requestEndDate",
        headerName: t("common.end-date"),
        flex: 1,
        rowSpanValueGetter: (value, row) =>
          row ? `${row.id}-${row.requestEndDate}` : value,
        valueFormatter: (value) => {
          if (value === null || value === undefined) return "-";

          return value
            ? formatWithCompanyTZ(
              value,
              dateTimeFormat || "dd/MM/yyyy HH:mm:ss"
            )
            : "-";
        },
      },
      {
        field: "leaveTypeId",
        headerName: t("common.leave-type"),
        flex: 1,
        valueGetter: (params) => {
          const leaevType = params;

          if (!leaevType) return "-";

          return getLeaveTypeLabel(leaevType);
        },
        renderCell: (params) => {
          const leaveTypeId = params.row?.leaveTypeId;

          const label = getLeaveTypeLabel(leaveTypeId);

          return (
            <Tooltip title={label} arrow>
              <span className="truncate">{label}</span>
            </Tooltip>
          );
        },
      },
      {
        field: "approvalStatus",
        headerName: t("common.approval"),
        flex: 1,

        valueGetter: (value, row) => {
          const approvalStatus = row?.approvalStatusId;

          if (!approvalStatus) return "-";

          if (approvalStatus === 1) return t("common.pending-request");
          if (approvalStatus === 2) return t("common.approved");
          if (approvalStatus === 3) return t("common.rejected");

          return String(approvalStatus);
        },

        renderCell: (params) => {
          const approvalStatusId = Number(params.row?.approvalStatusId);
          const label = params.value || "-";

          let color = "#6B7280";
          if (approvalStatusId === 1) color = "#EAB308";
          else if (approvalStatusId === 2) color = "#22C55E";
          else if (approvalStatusId === 3) color = "#EF4444";

          return (
            <Tooltip title={label} arrow>
              <span className="truncate" style={{ color }}>
                {label}
              </span>
            </Tooltip>
          );
        },
      },
      {
        field: "modifiedBy",
        headerName: t("common.approve-reject-by"),
        flex: 1,
        valueGetter: (value, row) => {
          const { approvalStatusId, modifiedBy } = row || {};

          if (approvalStatusId === 1) {
            return t("common.not-processed-yet");
          }

          if (!modifiedBy) return "-";

          return getFullNameByEmail(modifiedBy) || modifiedBy;
        },
        renderCell: (params) => {
          const { approvalStatusId, modifiedBy } = params.row;

          let label = "-";

          if (approvalStatusId === 1) {
            label = t("common.not-processed-yet"); // "chưa được xử lý"
          } else {
            label = getFullNameByEmail(modifiedBy);
          }

          return (
            <Tooltip title={label} arrow>
              <span className="truncate">{label}</span>
            </Tooltip>
          );
        },
      },

    ],
    [t, dateTimeFormat, getLeaveTypeLabel, getFullNameByEmail]
  );

  const handleDetail = useCallback((row) => {
    const info = mapRowToLeaveInfo(row);
    setSelectedLeave(info);

    const statusId = Number(row.approvalStatusId ?? row.requestStatusId);

    if (isLeavesMode || statusId === 2 || statusId === 3) {
      setOpenDetailAR(true);
    } else {
      setOpenDetail(true);
    }
  }, [mapRowToLeaveInfo, isLeavesMode]);

  const handleEdit = useCallback((row) => {
    setSelectedLeave(mapRowToLeaveInfo(row));
    setOpenEditLeave(true);
  }, [mapRowToLeaveInfo]);

  const handleDelete = useCallback((row) => {
    setSelectedLeave(mapRowToLeaveInfo(row));
    setOpenDeleteLeave(true);
  }, [mapRowToLeaveInfo]);

  const optionsColumn = useMemo(() => ({
    field: 'option',
    headerName: t('common.options'),
    width: 140,
    minWidth: 140,
    hideable: false,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    align: 'left',
    headerAlign: 'left',
    renderCell: (params) => {
      const row = params.row;
      const statusId = Number(row.approvalStatusId ?? row.requestStatusId);
      const isOwner = String(row.userId) === String(profile?.userId);
      const isDeleteLeaveRequest = String(row.requestType || "").toUpperCase() === "DELETE_LEAVE";
      const isProcessedRequest = statusId === 2 || statusId === 3;

      // Leaves: không phải chính chủ → admin chỉ thấy detail; chính chủ → edit + delete
      // Requests: approved/rejected thì admin và chính chủ đều xem được detail
      const showDetail = isLeavesMode
        ? isAdmin || isOwner
        : isRequestsMode && (isAdmin || (isOwner && isProcessedRequest));

      const showEdit = isLeavesMode
        ? isOwner
        : isRequestsMode && statusId === 1 && isOwner && !isDeleteLeaveRequest;

      const showDelete = isLeavesMode
        ? isOwner
        : isRequestsMode && statusId === 1 && isOwner;

      if (!showDetail && !showEdit && !showDelete) return null;

      const iconBtnClass =
        "inline-flex items-center justify-center w-8 h-8 shrink-0 rounded-lg hover:bg-gray-100 transition cursor-pointer p-0";

      return (
        <div
          className="flex flex-nowrap items-center justify-start gap-1 h-full w-full"
          onClick={(e) => e.stopPropagation()}
        >
          {showDetail && (
            <Tooltip title={t('button.details')} arrow>
              <button
                type="button"
                className={iconBtnClass}
                onClick={() => handleDetail(row)}
              >
                <InfoOutlinedIcon sx={{ fontSize: 20 }} className="text-[#0057FF]" />
              </button>
            </Tooltip>
          )}
          {showEdit && (
            <Tooltip
              title={isLeavesMode ? t('common.edit-request-leave') : t('button.edit')}
              arrow
            >
              <button
                type="button"
                className={iconBtnClass}
                onClick={() => handleEdit(row)}
              >
                <EditNew20pxSvg width={20} height={20} />
              </button>
            </Tooltip>
          )}
          {showDelete && (
            <Tooltip
              title={isLeavesMode ? t('common.request-delete-leave') : t('button.delete')}
              arrow
            >
              <button
                type="button"
                className={`${iconBtnClass} hover:bg-red-100`}
                onClick={() => handleDelete(row)}
              >
                <DeleteIcon16pxSvg width={20} height={20} color="#D00D00" />
              </button>
            </Tooltip>
          )}
        </div>
      );
    },
  }), [
    t,
    isLeavesMode,
    isRequestsMode,
    isAdmin,
    profile?.userId,
    handleDetail,
    handleEdit,
    handleDelete,
  ]);

  const displayColumns = useMemo(() => {
    let cols = [...baseColumns];
    if (isLeavesMode) {
      cols = cols.filter((c) => c.field !== 'approvalStatus' && c.field !== 'leavePolicyId');
      // cols.push({
      //   field: 'requestReason',
      //   headerName: t('common.requestReason'),
      //   flex: 1,
      //   valueGetter: (value, row) => row?.requestReason || '-',
      // });
      // cols.push({
      //   field: 'responseReason',
      //   headerName: t('common.responseReason'),
      //   flex: 1,
      //   valueGetter: (value, row) => row?.responseReason || '-',
      // });
    } else if (isRequestsMode) {
      cols.splice(1, 0, {
        field: 'requestType',
        headerName: t('common.type'),
        flex: 1,
        minWidth: 150,
        valueGetter: (value, row) => getRequestTypeLabel(row?.requestType ?? value),
      });
    }
    cols.push(optionsColumn);
    return cols.map((col) => ({
      ...col,
      renderHeader: col.renderHeader || renderHeaderWithTooltip,
      renderCell: col.renderCell || renderCellWithTooltip,
    }));
  }, [baseColumns, isLeavesMode, isRequestsMode, getRequestTypeLabel, optionsColumn, t]);

  const [rowData, setRowData] = useState([]);
  const [gridLoading,] = useState(false);
  const [, setSelectedRowId] = useState(null);
  const [rowCount, setRowCount] = useState(0);

  const [filteredRowData, setFilteredRowData] = useState([]);
  const [filteredRowCount, setFilteredRowCount] = useState(0);

  useEffect(() => {
    setTotalUserLeave?.(rowCount);
  }, [rowCount, setTotalUserLeave]);

  const getFromToByDateWithTZ = (date, systemTZ) => {
    // Setting Timezone lưu dạng offset (+07:00), không phải IANA → dùng utcOffset
    const offset = systemTZ || "+00:00";
    const base = dayjs(date).utcOffset(offset, true);

    return {
      from: base.startOf("day").utc().toISOString(),
      to: base.endOf("day").utc().toISOString(),
    };
  };

  useEffect(() => {
    const fetchData = async () => {
      show();
      try {
        const { from, to } = getFromToByDateWithTZ(selectedDate, genaralSettingTimeZone);

        const statusFilter = statusShowReject
          ? 2
          : formatLeaveFilter.selecStatusLeave !== 0
            ? formatLeaveFilter.selecStatusLeave
            : null;
        const requestStatusMap = {
          1: "APPROVED",
          2: "REJECTED",
          3: "PENDING",
        };
        const requestStatus = statusFilter ? requestStatusMap[statusFilter] : null;

        const payload = {
          pageNo: page + 1,
          recordPerPage: pageSize,
          from,
          to,
          groupId:
            formatLeaveFilter.group !== 1 ? formatLeaveFilter.group : null,
          userId: isAdmin
            ? formatLeaveFilter.userId
              ? formatLeaveFilter.userId.id
              : null
            : profile.userId,
          langId: localStorage.getItem("i18nextLngId"),
          ...(isRequestsMode
            ? { requestStatus }
            : {
              approvalStatusId: isLeavesMode ? 1 : statusFilter,
            }),
        };

        const response = isRequestsMode
          ? await getLeaveRequestList(payload)
          : await getLeaveList(payload);

        if (
          response.data.message === "Successful" ||
          response.data.message === "Thành công"
        ) {
          setRowCount(response.data.pagination.totalElements);
          const rawList = response.data.data || [];
          handleGetCountData(rawList.length);

          const leaveList = isRequestsMode
            ? rawList.map((item, index) => mapLeaveRequestToRow(item, index))
            : rawList.map((leave, index) => ({
              ...leave,
              id: leave.leaveId ?? index + 1,
              fullName: leave.fullName || leave.name || "-",
            }));

          if (isRequestsMode) {
            const pendingCount = leaveList.filter(
              (l) => Number(l.requestStatusId ?? l.approvalStatusId) === 3
            ).length;
            setPendingRequestCount?.(pendingCount);
          }

          setRowData(leaveList);
          const firstRow = leaveList?.[0];

          if (firstRow && onRowSelection && !isLeavesMode && !isRequestsMode) {
            const mapped = {
              leaveId: firstRow.leaveId,
              employeeCode: firstRow.employeeCode,
              name: firstRow.name,
              requestStartDate: formatWithCompanyTZ(
                firstRow.requestStartDate,
                fmt
              ),
              requestEndDate: formatWithCompanyTZ(
                firstRow.requestEndDate,
                fmt
              ),
              numOfTime: firstRow.numOfTime,
              hoursPerLeaveDaySnapshot: firstRow.hoursPerLeaveDaySnapshot,
              numOfDay: firstRow.numOfDay,
              leaveType: firstRow.leaveType,
              leavePolicy: firstRow.leavePolicy,
              approvalStatus: firstRow.approvalStatus,
              approveStartDate: firstRow.approveStartDate,
              approveEndDate: firstRow.approveEndDate,
              requestReason: firstRow.requestReason,
              responseReason: firstRow.responseReason,
              leavePolicyId: firstRow.leavePolicyId,
              leaveTypeId: firstRow.leaveTypeId,
              approvalStatusId: firstRow.approvalStatusId,
              userId: firstRow.userId,
              modifiedBy: firstRow.modifiedBy,
            };

            setRowSelectionModel([firstRow.leaveId]);
            setSelectedRowId(firstRow.leaveId);
            onRowSelection(mapped);
          } else if (onRowSelection) {
            setRowSelectionModel([]);
            onRowSelection({});
          }
          setFilteredRowData(leaveList);
          setFilteredRowCount(response.data.pagination.totalElements);
          hide();
        }
      } catch (error) {
        hide();
        console.error("LeavesGridInfoTable fetch error:", error);
        toast.error(error.response?.data?.message || error.message);
      } finally {
        hide();
      }
    };

    fetchData();
  }, [
    selectedDate,
    page,
    pageSize,
    refreshData,
    formatLeaveFilter,
    statusShowReject,
    gridMode,
    genaralSettingTimeZone,
    show,
    hide,
  ]);

  const CustomToolbar = useCallback(
    () => (
      isRequestsMode ? (
        <div>
          <GridToolbarContainer className="flex justify-between items-center mb-3">
            <div className="flex gap-3" />
            <CustomGridToolbarQuickFilter fullWidth />
          </GridToolbarContainer>
        </div>
      ) : null
    ),
    [isRequestsMode]
  );

  useEffect(() => {
    const fetchShifts = async () => {
      try {
        const response = await getShiftListAll(localStorage.getItem('i18nextLngId'));
        setShiftsData(response.data.listShift || []);
      } catch (error) {
        console.error(error);
      }
    };
    fetchShifts();
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const payload = {
          statusId: 1,
          page: 1,
          pageSize: 1000000,
          status: null,
          langId: localStorage.getItem("i18nextLngId")
        }
        const response = await getUserList(payload);
        const usersList = response.data.data;
        setAllUser(usersList);
      } catch (error) {
        console.log(error)
      }
    };
    fetchUser();

  }, [refreshData]);


  const onRowsSelectionHandler = (selectedIds) => {
    const selectedId = selectedIds[0];
    if (!selectedId) return;

    const selectedRow = rowData.find((row) => row.leaveId === selectedId);
    if (!selectedRow) return;

    onRowSelection?.({
      leaveId: selectedRow.leaveId,
      employeeCode: selectedRow.employeeCode,
      name: selectedRow.name,
      requestStartDate: formatWithCompanyTZ(
        selectedRow.requestStartDate,
        fmt
      ),
      requestEndDate: formatWithCompanyTZ(
        selectedRow.requestEndDate,
        fmt
      ),
      numOfTime: selectedRow.numOfTime,
      hoursPerLeaveDaySnapshot: selectedRow.hoursPerLeaveDaySnapshot,
      numOfDay: selectedRow.numOfDay,
      leaveType: selectedRow.leaveType,
      leavePolicy: selectedRow.leavePolicy,
      approvalStatus: selectedRow.approvalStatus,
      approveStartDate: selectedRow.approveStartDate,
      approveEndDate: selectedRow.approveEndDate,
      requestReason: selectedRow.requestReason,
      responseReason: selectedRow.responseReason,
      leavePolicyId: selectedRow.leavePolicyId,
      leaveTypeId: selectedRow.leaveTypeId,
      approvalStatusId: selectedRow.approvalStatusId,
      userId: selectedRow.userId,
      modifiedBy: selectedRow.modifiedBy,
    });
  };

  useEffect(() => {
    setRowSelectionModel([]);

    if (onRowSelection && !isLeavesMode && !isRequestsMode) {
      onRowSelection({});
    }
  }, [
    selectedDate,
    isLeavesMode,
    isRequestsMode,
    onRowSelection,
  ]);

  const defaultColumnVisibility = {
    name: true,
    approveStartDate: true,
    approveEndDate: true,
    numOfDay: true,
    leaveType: true,
    leavePolicy: true,
    approvalStatus: true,
  };

  const [columnVisibilityModel, setColumnVisibilityModel] = useState(
    defaultColumnVisibility
  );
  const emailUser = profile?.email;

  const handleColumnVisibilityChange = (newModel) => {
    setColumnVisibilityModel(newModel);
    Cookies.set(`LeavesGridInfoTable_${emailUser}`, JSON.stringify(newModel), {
      expires: 90,
      path: "/",
    });
  };

  useEffect(() => {
    const saved = Cookies.get(`LeavesGridInfoTable_${emailUser}`);

    if (saved) {
      setColumnVisibilityModel(JSON.parse(saved));
    }
  }, [emailUser]);

  const handleRowsPerPageChange = (newPageSize) => {
    const size =
      typeof newPageSize === "number"
        ? newPageSize
        : parseInt(newPageSize.target.value, 10);

    setPage((prevPage) => {
      const firstRowIndex = prevPage * pageSize;
      const newPage = Math.floor(firstRowIndex / size);
      const maxPage = Math.max(
        0,
        Math.ceil(filteredRowCount / size) - 1
      );

      return Math.min(newPage, maxPage);
    });

    setPageSize(size);
  };

  const hasRows = filteredRowData.length > 0;
  const gridHeight = hasRows ? 500 : 360;

  return (
    <>
      <div className="w-full min-w-0 max-w-full overflow-hidden">
        <div className={`w-full max-w-full ${hasRows ? 'overflow-x-auto' : 'overflow-x-hidden'}`}>
          <div style={{ width: "100%", overflowX: "auto" }}>
            <StyledDataGrid
              sx={{
                width: "100%",
                maxWidth: "100%",
                overflow: "hidden",
                height: gridHeight,
                "& .MuiDataGrid-scrollbar--vertical": { display: "none !important" },
                "& .MuiDataGrid-scrollbar--horizontal": {
                  display: hasRows ? undefined : "none !important",
                },
                "& .MuiDataGrid-virtualScroller": {
                  overflow: hasRows ? "auto !important" : "hidden !important",
                },
              }}
              rows={rowData}
              rowCount={rowCount}
              paginationMode="client"
              columns={displayColumns}
              loading={gridLoading}
              ignoreDiacritics
              slots={{
                toolbar: isRequestsMode ? CustomToolbar : undefined,
                pagination: CustomPagination,
                noRowsOverlay: NoRowOverlay,
                columnsPanel: CustomColumnsPanel,
              }}
              getRowId={(row) => row.leaveId ?? row.leaveRequestId ?? row.id}
              slotProps={{
                toolbar: {
                  showQuickFilter: true,
                },
                pagination: {
                  page,
                  count: filteredRowCount,
                  rowsPerPage: pageSize,
                  onPageChange: setPage,
                  onRowsPerPageChange: handleRowsPerPageChange,
                },
              }}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={handleColumnVisibilityChange}
              localeText={tableTranslated}
              rowSelectionModel={isLeavesMode || isRequestsMode ? [] : rowSelectionModel}
              onRowSelectionModelChange={(newSelection) => {
                if (isLeavesMode || isRequestsMode) return;
                setRowSelectionModel(newSelection);
                onRowsSelectionHandler(newSelection);
              }}
              initialState={{
                filter: {
                  filterModel: {
                    items: [],
                    quickFilterValues: [""],
                  },
                },
              }}
            />
          </div>
        </div>
      </div>

      {openDetailAR && selectedLeave && (
        <DetailRequestDialogAR
          onClose={() => { setOpenDetailAR(false); setSelectedLeave(null); }}
          leaveInfo={selectedLeave}
          onRefresh={handleRefreshData}
          leaveTypesData={leaveTypesData}
          isRequestMode={isRequestsMode}
        />
      )}
      {openDetail && selectedLeave && (
        <DetailRequestDialog
          onClose={() => { setOpenDetail(false); setSelectedLeave(null); }}
          leaveInfo={selectedLeave}
          onRefresh={handleRefreshData}
          leaveTypesData={leaveTypesData}
          isRequestMode={isRequestsMode}
        />
      )}
      {openEditLeave && selectedLeave && (
        <EditLeaveDialog
          open={openEditLeave}
          onClose={() => { setOpenEditLeave(false); setSelectedLeave(null); }}
          leaveInfo={selectedLeave}
          onRefresh={handleRefreshData}
          leaveTypesData={leaveTypesData}
          shiftsData={shiftsData}
          selectedDate={selectedDate}
          isLeaveMode={isLeavesMode}
        />
      )}
      {openDeleteLeave && selectedLeave && (
        <DeleteLeaveDialog
          onClose={() => { setOpenDeleteLeave(false); setSelectedLeave(null); }}
          onRefresh={handleRefreshData}
          leaveInfo={selectedLeave}
          isRequestMode={isRequestsMode}
          leaveTypesData={leaveTypesData}
        />
      )}
    </>
  );
};

export default LeavesGridInfoTable;
